import Component from '@glimmer/component';
import {
  PhCheckCircle,
  PhCircle,
  PhTrophy,
  PhStar,
  PhRocket,
  PhTarget,
  PhMedal,
  PhCrown,
} from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import style from 'ember-style-modifier/modifiers/style';
import { concat } from '@ember/helper';

/**
 * Milestone definition for debt journey
 */
export interface DebtMilestone {
  id: string;
  title: string;
  description: string;
  threshold: number; // percentage of debt paid
  achieved: boolean;
  achievedDate?: string;
  icon?: 'trophy' | 'star' | 'rocket' | 'target' | 'medal' | 'crown';
}

/**
 * MilestoneTracker - Achievement-style visualization of debt payoff milestones
 *
 * Shows a horizontal track of milestones with visual indicators for
 * achieved vs upcoming goals. Creates a gamified experience.
 *
 * @example
 * ```gts
 * <MilestoneTracker
 *   @milestones={{this.debtMilestones}}
 *   @currentProgress={{34}}
 * />
 * ```
 */
interface MilestoneTrackerSignature {
  Args: {
    /** Array of milestones to display */
    milestones: DebtMilestone[];
    /** Current progress percentage */
    currentProgress: number;
  };
  Element: HTMLDivElement;
}

const iconMap: Record<string, ComponentLike<{ Args: { size: number }; Element: SVGElement }>> = {
  trophy: PhTrophy,
  star: PhStar,
  rocket: PhRocket,
  target: PhTarget,
  medal: PhMedal,
  crown: PhCrown,
};

export default class MilestoneTracker extends Component<MilestoneTrackerSignature> {
  get nextMilestone(): DebtMilestone | undefined {
    return this.args.milestones.find((m) => !m.achieved);
  }

  get progressToNext(): number {
    if (!this.nextMilestone) return 0;
    return Math.round(Math.max(0, this.nextMilestone.threshold - this.args.currentProgress));
  }

  getIcon = (iconName?: string) => iconMap[iconName || 'star'] || PhStar;

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="tio-milestone-tracker" ...attributes>
      <div class="tio-milestone-tracker__header">
        <PhTrophy @size={{24}} class="tio-milestone-tracker__trophy" />
        <span class="tio-milestone-tracker__heading">Debt Freedom Milestones</span>
      </div>

      {{! Milestone Track }}
      <div class="tio-milestone-tracker__track">
        {{! Progress Line Background }}
        <div class="tio-milestone-tracker__line"></div>

        {{! Progress Line Fill }}
        <div
          class="tio-milestone-tracker__line-fill"
          {{style width=(concat @currentProgress "%")}}
        ></div>

        {{! Milestones }}
        <div class="tio-milestone-tracker__milestones">
          {{#each @milestones as |milestone|}}
            <div
              class="tio-milestone-tracker__milestone"
              {{style width=(concat "calc(100% / " @milestones.length ")")}}
            >
              {{! Milestone Circle }}
              <div
                class="tio-milestone-tracker__circle
                  {{if
                    milestone.achieved
                    'tio-milestone-tracker__circle--achieved'
                    'tio-milestone-tracker__circle--upcoming'
                  }}"
              >
                {{#if milestone.achieved}}
                  {{#let (this.getIcon milestone.icon) as |Icon|}}
                    <Icon @size={{22}} weight="fill" />
                  {{/let}}
                {{else}}
                  <PhCircle @size={{22}} />
                {{/if}}
              </div>

              {{! Milestone Label }}
              <div class="tio-milestone-tracker__label">
                <div
                  class="tio-milestone-tracker__title
                    {{if milestone.achieved 'tio-milestone-tracker__title--achieved'}}"
                >
                  {{milestone.title}}
                </div>
                <div class="tio-milestone-tracker__desc">
                  {{milestone.description}}
                </div>
                {{#if milestone.achievedDate}}
                  <div class="tio-milestone-tracker__date">
                    <PhCheckCircle @size={{12}} weight="fill" />
                    <span>{{milestone.achievedDate}}</span>
                  </div>
                {{/if}}
              </div>
            </div>
          {{/each}}
        </div>
      </div>

      {{! Next Milestone Callout }}
      {{#if this.nextMilestone}}
        <div class="tio-milestone-tracker__next">
          <div class="tio-milestone-tracker__next-row">
            <div class="tio-milestone-tracker__next-label">
              <div class="tio-milestone-tracker__next-icon">
                <PhTarget @size={{18}} />
              </div>
              <span class="tio-milestone-tracker__next-text">
                <span class="tio-milestone-tracker__next-text-label">Next milestone:</span>
                {{this.nextMilestone.title}}
              </span>
            </div>
            <span class="tio-milestone-tracker__next-value">
              {{this.progressToNext}}% to go
            </span>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
